// Copyright (c) StrangeBee 2023

import client, { queryClient } from '@Client';
import { StatusContext } from '@Context/StatusProvider';
import { downloadByUrl, handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import useOneQuery from '@Hooks/useOneQuery';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import { CaseExportDrawer } from '@Organisms/Case/CaseExport';
import CaseMergeDrawer from '@Organisms/Case/CaseMerge/CaseMergeDrawer';
import CaseStatusManage from '@Organisms/Case/CaseStatusManage/CaseStatusManage';
import CaseTitle from '@Organisms/Case/CaseTitle/CaseTitle';
import { computeEventList as computeCaseEventList } from '@Organisms/Case/eventList';
import { computeGetSelector } from '@Organisms/Case/selector';
import { computeListSelector } from '@Organisms/Organisation/selector';
import CaseLayoutSider from '@Organisms/Case/Siders/Layout';
import { ResponderDrawer } from '@Organisms/Responder';
import { CaseShareAdd } from '@Organisms/Shares/CaseShareAdd/CaseShareAdd';
import THEntityLayout from '@Organisms/THEntityLayout/THEntityLayout';
import ChildRoutes from '@Pages/Cases/Details/routes';
import { useCurrentUser } from '@Queries/userQueries';
import RouteBuilder from '@Services/RouteBuilder';
import { HeaderActionType, RouteType } from '@Types';
import { Badge } from 'antd';
import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { Redirect, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import {
    CaseOutput,
    CaseStatusStage,
    PageOutput,
    OrganisationOutput,
    PermissionsEnum,
    CaseReportAttachmentOutput,
} from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';
import { useGetAccessibleRoutes } from '@Helpers/getAccessibleRoutes';
import { useGenericQuery } from '@Queries/genericQueries';
import { ScopeContext } from '@Context/ScopeProvider';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import CaseBulkCaseTemplateManage from '@Organisms/Case/CaseBulkCaseTemplateManage/CaseBulkCaseTemplateManage';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { CaseReportCreate } from '@Organisms/Case/CaseReport/CaseReportCreate/CaseReportCreate';
import useListQuery from '@Hooks/useListQuery';
import { getAvailableLinks } from '@Helpers/caseSharing';

import './Details.scss';

type someCountType = Record<string, number>;

const CaseLayout = () => {
    const i = useLocalMessage();
    const history = useHistory();
    const { hasCortex, licenseCapabilities } = useContext(StatusContext);
    const { caseId } = useParams<{ caseId: string }>();
    const accessibleRoutes = useGetAccessibleRoutes(ChildRoutes);

    const { data: currentUserData } = useCurrentUser();

    const { organisations = [], organisation = '' } = currentUserData || {};

    const exportArchiveCapability = useMemo(
        () => !!licenseCapabilities?.case?.exportArchive || false,
        [licenseCapabilities],
    );

    const exportMispCapability = useMemo(() => !!licenseCapabilities?.misp || false, [licenseCapabilities]);

    const {
        data: caseData,
        refetch: getCaseRefetch,
        error,
    } = useOneQuery<CaseOutput>({
        selector: computeGetSelector({ caseId }),
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    const { refetch: pagesRefetch } = useListQuery<PageOutput[]>({
        controller: client.CaseController,
        queryParams: {
            selector: {
                head: [{ name: 'getCase', idOrName: caseId }, { name: 'pages' }],
                name: 'get-all-pages-from-case',
            },
            sort: ['+_createdAt'],
        },
    });

    const {
        customStatus: {
            case: { list: customStatusList },
        },
    } = useContext(ScopeContext);

    const { data: shareList = [], refetch: refetchShareList } = useGenericQuery({
        fetchCallback: () => eventList.shares.onList(caseId),
        key: ['sharings', caseId],
    });

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    useEffect(() => {
        getCaseRefetch();
    }, [caseId]); // eslint-disable-line

    const someCount = caseData?.extraData?.alerts.reduce((counter: someCountType, { type }): someCountType => {
        return counter[type] ? { ...counter, [type]: counter[type] + 1 } : { ...counter, [type]: 1 };
    }, {});

    const {
        number: caseNumber = 0,
        title = '',
        severity = 1,
        stage = '',
        flag,
        extraData,
        userPermissions = [],
    } = caseData || {};

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        if (title.includes('Import in progress')) {
            timeoutId = setInterval(() => {
                getCaseRefetch();
            }, 3000);
        }

        return () => {
            if (timeoutId) {
                clearInterval(timeoutId);
            }
        };
    }, [title]); // eslint-disable-line

    const { isOwner } = extraData || {};

    const hasPermissions = useSplitPermissions(userPermissions, [
        PermissionsEnum.MANAGE_CASE_REPORT,
        PermissionsEnum.MANAGE_CASE_CHANGE_OWNERSHIP,
        PermissionsEnum.MANAGE_CASE_UPDATE,
        PermissionsEnum.MANAGE_CASE_DELETE,
        PermissionsEnum.MANAGE_CASE_MERGE,
        PermissionsEnum.MANAGE_ACTION,
    ]);

    const links = useMemo(
        () => organisations.find(({ organisation: orgName }) => orgName === organisation)?.links,
        [organisation, organisations],
    );

    const availableLinks = useMemo(() => links && getAvailableLinks(links, orgList), [links, orgList]);

    const eventList = useMemo(
        () =>
            computeCaseEventList({
                controller: client.CaseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    getCaseRefetch();
                    pagesRefetch();
                },
            }),
        [getCaseRefetch, pagesRefetch, i],
    );

    const HeaderActions: HeaderActionType[] = [
        {
            label: i('case.layout.actions.flag'),
            icon: <ICONS.FlagOutlined />,
            Action: (triggerElem): ReactElement =>
                React.cloneElement(triggerElem, {
                    onClick: (): Promise<void | CaseOutput> => eventList.onEdit({ flag: true }, caseId),
                }),
            hidden: flag,
            disabled: !hasPermissions.updateCase,
        },
        {
            label: i('case.layout.actions.unflag'),
            icon: <ICONS.FlagFilled color="#e9cf42" />,
            className: 'actionlist-icon__flag',
            Action: (triggerElem): ReactElement =>
                React.cloneElement(triggerElem, {
                    onClick: (): Promise<void | CaseOutput> => eventList.onEdit({ flag: false }, caseId),
                }),
            hidden: !flag,
            disabled: !hasPermissions.updateCase,
        },
        {
            label: i('case.actions.bulk-caseTemplate.label'),
            icon: <ICONS.CaseTemplateIcon />,
            disabled: !hasPermissions.updateCase,
            Action: (triggerElem): ReactElement => {
                return (
                    <CaseBulkCaseTemplateManage
                        triggerElem={triggerElem}
                        organisation={organisation}
                        onEdit={(values) => {
                            eventList.onBulkCaseTemplate(values, [caseId]).then(() => {
                                queryClient.refetchQueries([`get-case-tasks-${caseId}`]);
                            });
                        }}
                    />
                );
            },
        },
        {
            label: i('case.layout.actions.merge'),
            icon: <ICONS.MergeIcon />,
            Action: (triggerElem): ReactElement => (
                <CaseMergeDrawer
                    triggerElem={triggerElem}
                    caseId={caseId}
                    listCasesForMerge={(filter): Promise<CaseOutput[]> =>
                        client.CaseController.list<CaseOutput[]>({
                            selector: {
                                head: [{ name: 'listCase' }],
                                name: 'get-cases-for-merge',
                                extraData: ['status'],
                            },
                            filters: [filter],
                            paginationOptions: { page: 0, itemsPerPage: 20 },
                        })
                    }
                />
            ),
            disabled: !hasPermissions.mergeCase,
        },
        {
            label: i('case.layout.actions.sharing'),
            icon: <ICONS.ShareAltOutlined />,
            hidden: !availableLinks?.length,
            disabled: !hasPermissions.updateCase,
            Action: (triggerElem): ReactElement => (
                <CaseShareAdd
                    triggerElem={triggerElem}
                    caseId={caseId}
                    onAddShare={({ taskRule, observableRule, sharingParameters = [] }, caseId) => {
                        const sharingParametersTmp = sharingParameters
                            .filter(({ isShared }) => isShared)
                            .map(({ isShared, ...rest }) => ({ ...rest })); // eslint-disable-line

                        eventList
                            .onEdit({ taskRule, observableRule }, caseId)
                            .then(() => eventList.shares.onBulkEdit?.(sharingParametersTmp, caseId))
                            .finally(() => refetchShareList());
                    }}
                    shareList={shareList}
                    currentOrganisation={currentUserData?.organisation}
                />
            ),
        },
        {
            label: i('case.layout.actions.export'),
            disabled: !exportArchiveCapability && !exportMispCapability,
            icon: (
                <Badge count={someCount?.misp || 0} className="badge-export" size="small" overflowCount={10}>
                    <ICONS.UploadOutlined />
                </Badge>
            ),
            Action: (triggerElem): ReactElement => (
                <CaseExportDrawer
                    triggerElem={triggerElem}
                    onExportToMisp={(servers) => {
                        Promise.all(
                            servers.map(({ name }) =>
                                client.MispController.export({
                                    caseId,
                                    server: name,
                                }).catch(handleNotificationError),
                            ),
                        );
                    }}
                    disabledExportFile={!exportArchiveCapability}
                    disabledMisp={!exportMispCapability}
                    getExportUrl={(password?: string) => {
                        return eventList.getExportUrl(caseId, password || '');
                    }}
                />
            ),
        },
        {
            label: i('case.layout.actions.create-report'),
            icon: <ICONS.ReportTemplateIcon />,
            hidden: !hasPermissions.casereport,
            Action: (triggerElem): ReactElement => (
                <CaseReportCreate
                    onFinish={(data) =>
                        eventList.onCreateReport(data, caseId).then((data) => {
                            downloadByUrl(
                                (data as CaseReportAttachmentOutput).filename,
                                window.location.origin +
                                    client.apiUrl +
                                    `/v1/caseReport/${(data as CaseReportAttachmentOutput)._id}/download`,
                            );
                            history.push(
                                RouteBuilder.compute('cases.attachments-section', { caseId, sectionId: 'reports' }),
                            );
                            return data;
                        })
                    }
                    triggerElem={triggerElem}
                    eventList={eventList}
                />
            ),
        },
        {
            label: i('case.layout.actions.close'),
            icon: <ICONS.CloseIcon />,
            className: 'actionlist-icon__warning',
            hidden: stage === CaseStatusStage.CLOSED,
            disabled: !hasPermissions.updateCase,
            Action: (triggerElem): ReactElement => (
                <CaseStatusManage
                    triggerElem={triggerElem}
                    data={caseData}
                    onEdit={eventList.onEdit}
                    caseId={caseId}
                    closed={true}
                    statusList={customStatusList}
                />
            ),
        },
        {
            label: i('case.layout.actions.reopen.label'),
            icon: <ICONS.CheckIcon />,
            hidden: stage !== CaseStatusStage.CLOSED,
            disabled: !hasPermissions.updateCase,
            Action: (triggerElem): ReactElement => (
                <CaseStatusManage
                    triggerElem={triggerElem}
                    data={caseData}
                    onEdit={eventList.onEdit}
                    caseId={caseId}
                    closed={false}
                    statusList={customStatusList}
                />
            ),
        },
        {
            label: i('case.layout.actions.delete.label'),
            icon: <ICONS.DeleteIcon />,
            className: 'actionlist-icon__warning',
            disabled: !hasPermissions.deleteCase,
            hidden: !isOwner,
            Action: (triggerElem): ReactElement => (
                <THPopConfirm
                    title={i('case.layout.actions.delete.confirm')}
                    onConfirm={() =>
                        eventList.onDelete(caseId).then(() => history.push(RouteBuilder.compute('cases.default')))
                    }
                >
                    {triggerElem}
                </THPopConfirm>
            ),
        },
        {
            label: i('case.layout.actions.responders'),
            icon: <ICONS.SettingIcon />,
            disabled: !hasPermissions.action,
            Action: (triggerElem): ReactElement => (
                <ResponderDrawer
                    triggerElem={triggerElem}
                    objectId={caseId}
                    objectType="case"
                    objectData={{ caseNumber: caseNumber + '' }}
                />
            ),
            hidden: !hasCortex,
        },
    ];

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.CASE },
        (data) => {
            const canRefetch = data.some(
                (audit) => audit.base.action === 'update' && audit.base._createdBy !== currentUserData?.login,
            );
            if (canRefetch) {
                getCaseRefetch();
            }
        },
        [currentUserData?.login],
    );
    useStreamObserver(
        { objectType: STREAM_EVENTS.TASK },
        (data) => {
            const canRefetch = data.some((audit) => audit.base.details?.assignee != undefined);
            if (canRefetch) {
                getCaseRefetch();
            }
        },
        [],
    );

    if (error) {
        openMessage('error', i('notification.case.doesnotExist'));
        return <Redirect to={RouteBuilder.compute('cases.default')} />;
    }

    return (
        <THEntityLayout
            className="layout-case"
            routes={accessibleRoutes}
            leftSiderContent={
                <CaseLayoutSider caseId={caseId} data={caseData} eventList={eventList} routes={accessibleRoutes} />
            }
            title={
                <CaseTitle
                    caseNumber={caseNumber}
                    title={title}
                    severity={severity}
                    editTitle={(value): Promise<void | CaseOutput> => eventList.onEdit({ title: value }, caseId)}
                    isFilled
                    readOnly={!hasPermissions}
                />
            }
            actions={HeaderActions}
            entityId={caseId}
        />
    );
};

const BreadcrumbLabel = (): ReactElement => {
    const match = useRouteMatch('/cases/:caseId');

    const { caseId = '' } = (match?.params as any) || {};

    const { data: caseData } = useOneQuery<CaseOutput>({
        selector: computeGetSelector({ caseId }),
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    return <>{`#${caseData?.number}`}</>;
};

export const Route: RouteType = {
    path: RouteBuilder.compute('cases.layout'),
    content: <CaseLayout />,
    space: ['user'],
    exact: false,
    breadcrumb: {
        label: <BreadcrumbLabel />,
        isLink: false,
    },
};
